import { BidParams } from './bid-params-mapping'
import { BidParamSpec, BidParamType } from './bidderCatalog'

export const SHOULD_ONLY_EDIT_BID_PARAMS_MESSAGE = 'Please make sure you only edited the bid parameters.'

export enum BidderParamValidationType {
  MISSING_PARAMS = 'missing-params',
  WRONG_TYPE = 'wrong-type',
  DUPLICATE_ENTRY = 'duplicate-entry',
}

type BidderParamValidationPayload = {
  [BidderParamValidationType.MISSING_PARAMS]: {
    paramName: string
  }
  [BidderParamValidationType.WRONG_TYPE]: {
    paramName: string
    expectedType: BidParamType
  }
  [BidderParamValidationType.DUPLICATE_ENTRY]: {
    siteName?: string
    adUnitName?: string
    device?: string
    mediaType?: string
  }
}

export type BidderParamValidation<Type extends BidderParamValidationType = BidderParamValidationType> = { line: number | undefined } & {
  type: Type
  detail: BidderParamValidationPayload[Type]
}

export class BidderValidationError extends Error {}

const BOOLEAN_TRUE_TOKENS = ['true', '1', 'x']
const BOOLEAN_FALSE_TOKENS = ['false', '0']

/**
 * Read a `boolean` bid param, whatever the spreadsheet or the CSV holds.
 *
 * Mappings are filled in by hand and in Excel, so `=== 'true'` is not enough. `x` is the mark the
 * spreadsheets already use for a flag (see `PBSTCK_BIDDER_DEFAULT_BID_PARAMS`), `1` and `0` come from a
 * column formatted as a number, and Excel often writes `TRUE`.
 *
 * Anything else returns `undefined`, so the publisher gets a type error, instead of `false`, which would
 * silently turn a flag off.
 */
export const parseBooleanBidParam = (value: string | number | boolean): boolean | undefined => {
  if (typeof value === 'boolean') return value
  const token = String(value).trim().toLowerCase()
  if (BOOLEAN_TRUE_TOKENS.includes(token)) return true
  return BOOLEAN_FALSE_TOKENS.includes(token) ? false : undefined
}

export const validateExpectedBidderParamType = (
  value: string | undefined,
  param: BidParamSpec
): BidderParamValidation<BidderParamValidationType.WRONG_TYPE | BidderParamValidationType.MISSING_PARAMS> | undefined => {
  if (!param.required && !value) return

  if (!value) {
    return {
      line: undefined,
      type: BidderParamValidationType.MISSING_PARAMS,
      detail: {
        paramName: param.name,
      },
    }
  }

  const validation: BidderParamValidation<BidderParamValidationType.WRONG_TYPE> = {
    line: undefined,
    type: BidderParamValidationType.WRONG_TYPE,
    detail: {
      paramName: param.name,
      expectedType: param.type,
    },
  }

  switch (param.type) {
    case 'string': {
      break
    }
    case 'number': {
      const numValue = parseInt(value)
      if (isNaN(numValue)) {
        return validation
      }
      break
    }
    case 'array': {
      if (typeof value !== 'string') {
        return validation
      }
      if (!(value as string).match(/\[.*?\]/)) {
        return validation
      }
      break
    }
    case 'boolean': {
      if (parseBooleanBidParam(value) === undefined) {
        return validation
      }
      break
    }
    default: {
      throw new BidderValidationError(`Bid param from catalog has an unknown type : ${param.type}`)
    }
  }
}

/**
 * Denormalize a bidder param string value to its expected type
 */
export const denormalizeBidderParamString = (param: BidParamSpec, value: string): string | number | string[] | boolean => {
  const CSV_ARRAY_DELIMITER = ',' as const
  switch (param.type) {
    case 'string': {
      return String(value)
    }
    case 'number': {
      return parseInt(value)
    }
    case 'array': {
      // we receive a string formated as "[value1, value2, value3]", we need to remove the brackets and split on the delimiter
      // quotes are required around arrays to be CSV compliant, but the parser removes them automatically, we don't need to do anything for them
      return (
        value
          .slice(1, -1) // remove brackets
          .split(CSV_ARRAY_DELIMITER)
          // trim each value and remove surrounding single quotes if needed
          .map((v: string) => v.trim().replace(/^['|’|‘](.*)['|‘|’]$/, '$1'))
      )
    }
    case 'boolean': {
      // Text we cannot read keeps its value instead of becoming `false`: validation already flagged it,
      // and a silent `false` would look like a deliberate one.
      return parseBooleanBidParam(value) ?? value
    }
    default: {
      throw new Error(`Bid param from catalog has an unexpected type : ${param.type}. Might be a database error.`)
    }
  }
}

export const denormalizeBidderParam = (param: BidParamSpec, value: string | number | boolean | string[] | BidParams): string | number | boolean | string[] | string[] | BidParams => {
  switch (param.type) {
    case 'number': {
      return typeof value === 'string' ? parseInt(value) : value
    }
    case 'boolean': {
      // Text we cannot read keeps its value instead of becoming `false`: validation already flagged it,
      // and a silent `false` would look like a deliberate one.
      const readable = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      return readable ? (parseBooleanBidParam(value) ?? value) : value
    }
    case 'string': {
      return String(value)
    }
    case 'array': {
      // we receive a string formated as "[value1, value2, value3]", we need to remove the brackets and split on the delimiter
      // quotes are required around arrays to be CSV compliant, but the parser removes them automatically, we don't need to do anything for them
      return typeof value === 'string'
        ? value
            .slice(1, -1) // remove brackets
            .split(',')
            // trim each value and remove surrounding single quotes if needed
            .map((v: string) => v.trim().replace(/^['|’|‘](.*)['|‘|’]$/, '$1'))
        : value
    }
    default: {
      return value
    }
  }
}
